// Libraries
import React from 'react';

// Supermove
import {Styled, Space, ScrollView, Icon, Loading} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useModal, useNavigationDOM, useQuery, useToggle, MutationError} from '@supermove/hooks';
import {ProjectType, ProjectTypeModel} from '@supermove/models';
import {colors, Typography} from '@supermove/styles';

// App
import EditPanel from '@shared/design/components/Panel/EditPanel';
import ProjectTypeFeatureForm from '@shared/modules/Project/forms/ProjectTypeFeatureForm';
import useUpdateProjectTypeFeatureMutation from '@shared/modules/Project/hooks/useUpdateProjectTypeFeatureMutation';
import SidebarPageV2 from 'modules/App/components/SidebarPageV2';
import ProjectTypeSettingsPageHeader from 'modules/Organization/Settings/ProjectTypes/components/ProjectTypeSettingsPageHeader';
import ProjectTypeValuationCoverageBillingPanel from 'modules/Organization/Settings/ProjectTypes/components/ProjectTypeValuationCoverageBillingPanel';
import ProjectTypeValuationCoverageBillingPanelEdit from 'modules/Organization/Settings/ProjectTypes/components/ProjectTypeValuationCoverageBillingPanelEdit';
import ProjectTypeValuationCoverageContent from 'modules/Organization/Settings/ProjectTypes/components/ProjectTypeValuationCoverageContent';

const PageContainer = Styled.View`
  flex: 1;
`;

const PageContentContainer = Styled.View`
  flex: 1;
  padding-left: 24px;
  background-color: ${colors.gray.background};
`;

const HeaderText = Styled.Text`
  ${Typography.Label1}
`;

const CreateButton = Styled.ButtonV2`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 12px;
  padding-vertical: 8px;
  background-color: ${colors.blue.interactive};
  border-radius: 4px;
  width: fit-content;
`;

const CreateButtonText = Styled.Text`
  ${Typography.Label2}
  color: ${colors.white};
`;

const BillingSection = ({
  projectType,
  refetch,
}: {
  projectType: ProjectTypeModel;
  refetch: () => void;
}) => {
  const editInventorySettingsToggle = useToggle();
  const {form, submitting, handleSubmit} = useUpdateProjectTypeFeatureMutation({
    projectTypeFeatureForm: ProjectTypeFeatureForm.new({
      projectTypeId: projectType.id,
      feature: ProjectType.FEATURES.TAX_VALUATION_COVERAGE,
      isEnabled: projectType.features.taxValuationCoverage,
    }),
    onSuccess: () => {
      editInventorySettingsToggle.handleToggleOff();
      refetch();
    },
    onError: (errors: MutationError[]) => console.log({errors}),
  });

  return (
    <EditPanel
      bodyComponentProps={{projectType}}
      editBodyComponentProps={{
        form,
        field: 'projectTypeFeatureForm',
        projectType,
      }}
      BodyComponent={ProjectTypeValuationCoverageBillingPanel}
      EditBodyComponent={ProjectTypeValuationCoverageBillingPanelEdit}
      title={'Billing'}
      handleSave={handleSubmit}
      isSubmitting={submitting}
      handleCancel={form.handleReset}
      isEditing={editInventorySettingsToggle.isOn}
      handleEdit={editInventorySettingsToggle.handleToggleOn}
      handleClose={editInventorySettingsToggle.handleToggleOff}
      SaveButtonIconComponent={Icon.Check}
    />
  );
};

const ProjectTypeValuationCoverageSettingsPageContent = ({projectTypeUuid}: any) => {
  const uploadValuationCoverageTableModal = useModal({
    name: 'Upload Valuation Coverage Table Modal',
  });
  const {loading, data, refetch} = useQuery(ProjectTypeValuationCoverageSettingsPage.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      projectTypeUuid,
    },
  });

  return (
    <Loading loading={loading}>
      {() => {
        const {projectTypeByUuid, viewer} = data;
        const {viewingOrganization: organization} = viewer;

        return (
          <PageContainer>
            <ProjectTypeSettingsPageHeader projectType={projectTypeByUuid} />
            <PageContentContainer>
              <ScrollView>
                {organization.features.isEnabledBillingGenerateTaxableItems && (
                  <React.Fragment>
                    <Space height={24} />
                    <BillingSection projectType={projectTypeByUuid} refetch={refetch} />
                  </React.Fragment>
                )}
                <Space height={24} />
                <HeaderText>Configure Valuation Coverage</HeaderText>
                <Space height={12} />
                <CreateButton onPress={uploadValuationCoverageTableModal.handleOpen}>
                  <CreateButtonText>Update Valuation Coverage</CreateButtonText>
                </CreateButton>
                <ProjectTypeValuationCoverageContent
                  projectTypeUuid={projectTypeUuid}
                  uploadValuationCoverageTableModal={uploadValuationCoverageTableModal}
                />
              </ScrollView>
            </PageContentContainer>
          </PageContainer>
        );
      }}
    </Loading>
  );
};

const ProjectTypeValuationCoverageSettingsPage = () => {
  const {params} = useNavigationDOM();

  return (
    <SidebarPageV2 selected={'settings'}>
      <ProjectTypeValuationCoverageSettingsPageContent projectTypeUuid={params.projectTypeUuid} />
    </SidebarPageV2>
  );
};

ProjectTypeValuationCoverageSettingsPage.query = gql`
  ${ProjectTypeSettingsPageHeader.fragment}
  ${ProjectTypeValuationCoverageBillingPanel.fragment}

  query ProjectTypeValuationCoverageSettingsPage(
    $projectTypeUuid: String!,
  ) {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        features {
          isEnabledBillingGenerateTaxableItems: isEnabled(feature: "BILLING_GENERATE_TAXABLE_ITEMS")
        }
      }
    }
    projectTypeByUuid(projectTypeUuid: $projectTypeUuid) {
      id
      features {
        taxValuationCoverage
      }
      ...ProjectTypeSettingsPageHeader
      ...ProjectTypeValuationCoverageBillingPanel
    }
  }
`;

export default ProjectTypeValuationCoverageSettingsPage;
